import React, { useState, useEffect, useCallback, useMemo } from "react";
import { TextFieldProps } from "./TextField.types";
import {
  Description,
  ErrorIcon,
  ErrorMessage,
  FieldWrapper,
  Input,
  Label,
} from "./TextField.style";

const TextField: React.FC<TextFieldProps> = ({
  id,
  type,
  label,
  errorMessage,
  placeholder,
  isRequired,
  value,
  onChange,
  onValidityChange,
  requiredLabel,
  description,
  disable,
  minValue = 0,
  customValidator
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [cleanValue, setCleanValue] = useState(value || "");
  const [isValid, setIsValid] = useState(false);
  const [touched, setTouched] = useState(false);

  const today = useMemo(() => new Date(), []); // Memoriza a data atual

  const maxDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(today.getFullYear() - 18);
    return date;
  }, [today]);

  const minDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(today.getFullYear() - 70);
    return date;
  }, [today]);

  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isValid);
    }
  }, [isValid, onValidityChange]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    let newCleanValue = "";

    if (type === "tel") {
      const formattedValue = formatPhoneNumber(newValue);
      newCleanValue = formattedValue.replace(/\D/g, '');
      setInputValue(formattedValue);
    } else if (type === "number") {
      const formattedValue = formatCurrency(newValue);
      newCleanValue = formattedValue
        .replace(/R\$\s?/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
      setInputValue(formattedValue);
    } else {
      newCleanValue = newValue;
      setInputValue(newValue);
    }

    setCleanValue(newCleanValue);

    const isValidNow = validateValue(newCleanValue);
    if (onChange) onChange(newCleanValue, isValidNow);
  };

  const handleFocus = () => {
    setTouched(false);
    setIsValid(true);
  };

  const handleBlur = () => {
    setTouched(true);
    const isValidNow = validateValue(inputValue);
    if (onValidityChange) onValidityChange(isValidNow);
  };

  const formatPhoneNumber = (value: string) => {
    const numericValue = value.replace(/\D/g, '');

    if (numericValue.length < 11) {
      return value; 
    }

    if (numericValue.length <= 2) {
      return numericValue;
    } else if (numericValue.length <= 7) {
      return `(${numericValue.slice(0, 2)}) ${numericValue.slice(2)}`;
    } else {
      return `(${numericValue.slice(0, 2)}) ${numericValue.charAt(2)}${numericValue.slice(3, 7)}-${numericValue.slice(7, 11)}`;
    }
  };

  const formatCurrency = (value: string) => {
    const numericValue = Number(value.replace(/\D/g, ""));
    const numberValue = numericValue / 100;
    return `R$ ${numberValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const validateValue = useCallback((value: string) => {
    let valid = true;

    if (isRequired && value.trim() === "") {
      valid = false;
    } else if (customValidator) {
      valid = customValidator(value);
        } else if (type === "email") {
      const regex = /^[A-Za-z0-9+&'*.\-/^{}~_]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/;
      const atSymbolCount = (value.match(/@/g) || []).length;
      valid = regex.test(value) && atSymbolCount === 1 && value.includes("@");
    } else if (type === "tel") {
      const regex = /^(?!0)(\d{2})(9\d{8})$/;
      valid = regex.test(cleanValue);
    } else if (type === "number") {
      if (cleanValue === "0.00") {
        valid = true;
        setCleanValue("");
      } else {
        const regex = /^[0-9]+(\.[0-9]{1,2})?$/;
        valid = regex.test(cleanValue) && Number(cleanValue) > minValue;
      }
    } else if (type === "text") {
      const hasLetter = /^[A-Za-zÀ-ÿ\s-]*$/.test(value);
      const isLengthValid = value.length >= 3 && value.length < 100;
      valid = hasLetter && isLengthValid;
    } else if (type === "date") {
      const inputDate = new Date(cleanValue);
      valid = !!cleanValue && inputDate >= minDate && inputDate <= maxDate;
    }

    setIsValid(valid);
    return valid;
  }, [isRequired, customValidator, cleanValue, minValue, type, minDate, maxDate]);

  useEffect(() => {
    if (value) {
      validateValue(value);
      if (type === "tel") {
        setInputValue(formatPhoneNumber(value));
      }
    }
  }, [value, type, validateValue]);

  return (
    <FieldWrapper className="textField">
      {label && <Label htmlFor={id}>{label + (isRequired ? "" : "")}</Label>}
      <div className="input__wrapper">
        <Input
          $borderColor={
            (!disable && isRequired && touched && cleanValue.trim() === "") ||
            (touched && !isValid && cleanValue.trim() !== "")
          }
          type={type === "number" ? "text" : type}
          placeholder={placeholder}
          id={id}
          required={isRequired}
          value={inputValue}
          onChange={handleChange}
          disabled={disable}
          inputMode={
            type === "number" || type === "date" || type === "tel"
              ? "numeric"
              : "text"
          }
          min={
            type === "date" ? minDate.toISOString().split("T")[0] : undefined
          }
          max={type === "date" ? maxDate.toISOString().split("T")[0] : undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {type !== "date" &&
          ((touched && !isValid && cleanValue.trim() !== "") ||
            (isRequired && touched && cleanValue.trim() === "")) && (
            <ErrorIcon role="img" aria-label="error" />
          )}
      </div>
      {!disable && touched && !isValid && errorMessage && cleanValue.trim() !== "" && (
        <ErrorMessage aria-live="assertive" role="alert">{errorMessage}</ErrorMessage>
      )}
         
      {!disable && isRequired && touched && cleanValue.trim() === "" && (
        <ErrorMessage aria-live="assertive" role="alert">{requiredLabel}</ErrorMessage>
      )}
      {description && <Description>{description}</Description>}
    </FieldWrapper>
  );
};

export default TextField;